import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/regulated-waste-disposal-page.module.css'
// eslint-disable-next-line import/extensions
import regulatedWaste from '../../json/regulated-waste-disposal-data.json'

const regulatedServiceData = {
  name: 'Regulated Waste Disposal',
  description:
    'Compliant disposal of regulated waste for businesses. Dimension is the catch-all for EPA- and DOT-regulated materials — universal waste, batteries, mercury devices, aerosols, antifreeze, gases, lab packs, and corrosive, oxidizing, or reactive liquids — with full manifesting and documentation.',
  serviceType: 'Regulated Waste Disposal',
  url: 'https://getdimension.com/regulated-waste-disposal',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Universal Waste and Battery Disposal' },
    { name: 'Aerosol and Spray Can Disposal' },
    { name: 'Antifreeze and Coolant Disposal' },
    { name: 'Lab Pack and Clinical Debris Disposal' },
    { name: 'Corrosive, Oxidizing and Reactive Liquid Disposal' },
  ],
}

const regulatedFAQData = {
  questions: regulatedWaste.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function RegulatedWasteDisposal() {
  return (
    <>
      <Head>
        <title>Regulated Waste Disposal for Businesses | Dimension</title>
        <meta
          name="description"
          content="Compliant regulated waste disposal for businesses — universal waste, batteries, aerosols, antifreeze, gases, lab packs, corrosive & reactive liquids. EPA/DOT documented, instant quote, 30% less than competitor rates."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/regulated-waste-disposal" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={regulatedServiceData} />
        <SchemaMarkup type="FAQPage" data={regulatedFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heading={regulatedWaste.pitch.heading}
          material={regulatedWaste.pitch.material}
          pitchText={regulatedWaste.pitch.pitchText}
          ctaLink={regulatedWaste.pitch.ctaLink}
          ctaText={regulatedWaste.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...regulatedWaste.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...regulatedWaste.benefits} />
        <CaseStudy {...regulatedWaste.caseStudy} />
        <Approach {...regulatedWaste.approach} />
        <FAQ {...regulatedWaste.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
            {
              label: 'Flammable Waste Disposal',
              href: '/flammable-waste-disposal',
              description:
                'Compliant disposal of solvents, used fuel, aerosols, and other DOT Class 3 ignitable waste.',
            },
            {
              label: 'Paint Recycling and Disposal',
              href: '/paint-disposal',
              description:
                'Compliant pickup and recycling of latex, oil-based, and spray paint for businesses.',
            },
            {
              label: 'Oil Recycling and Used Oil Pickup',
              href: '/oil-recycling',
              description:
                'Eco-friendly used cooking, motor, and hydraulic oil recycling via removal or on-site pumping.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default RegulatedWasteDisposal
